import {BehaviorSubject, combineLatest} from 'rxjs';
import {filter, map, shareReplay, startWith, throttleTime} from 'rxjs/operators';
import {fixLink$} from '@/store';
import {get} from 'svelte/store';
import {mc} from '@/core';
import {sd} from '@/da/sd';
import {LINKS} from '@/const/link';
import {wwData$} from '@/streams/worker-data';
import {userInfo$} from './user';
import type {ContractAccount, UContractAccounts, UAssetsContract, UElTrack} from '@px/ws-data';

// total assets
export const contractTotalAssets$ = getAssets('account-assets');
export const pilotContractTotalAssets$ = getAssets('pilot-contract-assets');
export const contractTotalAssetsAll$ = getAssets('account-assets-all');
export const botTradeAssets$ = getAssets('account-assets-bot-trade');
export const copyTradeAssets$ = getAssets('account-assets-copy-trade');

const fixLink = get(fixLink$);

export const contractAccountsV0$ = wwData$.pipe(
  filter((o) => o.type === 'accounts'),
  map((o: UContractAccounts) => <ContractAccount[]>o.data.accounts),
  map((accounts) => {
    return accounts.map((account) => {
      const tradeLink = !['USD', 'USDT', 'USDC'].includes(account.currency)
        ? LINKS.PHEMEX_CONTRACT_INVERSE_TRADE + '/' + account.currency + 'USD'
        : LINKS.PHEMEX_CONTRACT_TRADE + '/BTCUSD' + (['USDT'].includes(account.currency) ? 'T' : '');
      return {...account, tradeLink, ...convertAction(account)};
    });
  }),
  startWith([]),
  shareReplay(1)
);

export const isSupportPilotContract$ = userInfo$.pipe(
  map((userInfo) => userInfo.isMain),
  startWith(true)
);
export const pilotContractAccounts$ = wwData$.pipe(
  filter((o) => o.type === 'pilot-accounts'),
  map((o: UContractAccounts) => <ContractAccount[]>o.data.accounts),
  map((accounts) => {
    return accounts.map((account) => {
      const tradeLink = account.currency === 'PUSD' ? fixLink(LINKS.PHEMEX_PILOT_CONTRACT_TRADE) : '';
      return {...account, tradeLink, ...convertAction(account)};
    });
  }),
  startWith([]),
  shareReplay(1)
);

export const contractAccounts$ = combineLatest([contractAccountsV0$, pilotContractAccounts$]).pipe(
  throttleTime(150),
  map(([accounts, pilotContractAccounts]) => [...accounts, ...pilotContractAccounts]),
  startWith([]),
  shareReplay(1)
);

export const copyTradeAccountMap$ = getAccountMap('accounts-copy-trade');
export const botTradeAccountMap$ = getAccountMap('accounts-bot-trade');

export const contractPTDiscountState$ = new BehaviorSubject<boolean>(false);

wwData$
  .pipe(
    filter((o) => o.type === 'contract-pt-discount-state'),
    map((o) => o.data.state)
  )
  .subscribe((state) => contractPTDiscountState$.next(state));

function getAccountMap(type: string) {
  return wwData$.pipe(
    filter((o) => o.type === type),
    map((o) => o.data),
    startWith(new Map<number, ContractAccount[]>()),
    shareReplay(1)
  );
}

function getAssets(type: string) {
  return wwData$.pipe(
    filter((o) => o.type === type),
    map((o: UAssetsContract) => o.data.assets),
    startWith(initAssets())
  );
}

function initAssets() {
  const BTC_PRECISION = 8;
  const USD_PRECISION = 2;
  return {
    isInit: true,
    btcEv: 0,
    btc: (0).toFixed(BTC_PRECISION),
    usdEv: 0,
    usd: (0).toFixed(USD_PRECISION),
    money: (0).toFixed(USD_PRECISION),
    usdtEv: 0,
    usdt: (0).toFixed(USD_PRECISION),
    bonusBtcEv: 0,
    bonusBtc: (0).toFixed(BTC_PRECISION),
    bonusUsdEv: 0,
    bonusUsd: (0).toFixed(USD_PRECISION),
    bonusMoney: (0).toFixed(USD_PRECISION),
    bonusUsdtEv: 0,
    bonusUsdt: (0).toFixed(USD_PRECISION),
  };
}

wwData$.pipe(filter((o) => o.type === 'et-track')).subscribe((o: UElTrack) => {
  if (o.data.params && o.data.params.phase === 'ws_error' && o.data.params.content) {
    try {
      const content = JSON.parse(o.data.params.content);
      if (content?.code === 6019) {
        mc.send('toast', 'error', {message: 'Too many requests, Please try again later'});
      }
    } catch {}
  }
  sd.track(o.data.event, o.data.params);
});

function convertAction(account) {
  return {
    canTrade: account.currency === 'PT' ? false : true,
    canConvert: account.currency === 'PT' ? false : true,
    canTransferIn: true,
    canTransferOut: true,
  };
}
